import { useEffect, useState, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Module-level signal so PageSkeleton can tell the bar when Suspense resolves.
 * - signalSlowNavigation(): called when PageSkeleton mounts (chunk is loading)
 * - signalNavigationComplete(): called when PageSkeleton unmounts (chunk loaded)
 */
let _complete: (() => void) | null = null
let _cancelFastTimeout: (() => void) | null = null

export function signalSlowNavigation() {
  _cancelFastTimeout?.()
}

export function signalNavigationComplete() {
  _complete?.()
}

/**
 * Thin progress bar at the top of the viewport during route transitions.
 *
 * Completion strategy:
 * 1. Fast navigation (chunk cached, no Suspense): completes after 500ms fallback
 * 2. Slow navigation (chunk loading, Suspense triggers): PageSkeleton signals
 *    on mount (cancel fast timeout) and unmount (complete the bar)
 * 3. Safety: auto-completes after 10s regardless
 */
export function PageLoadingBar() {
  const location = useLocation()
  const [progress, setProgress] = useState(0)
  const [visible, setVisible] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const fastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevPath = useRef(location.pathname)

  const complete = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
    if (fastTimeoutRef.current) { clearTimeout(fastTimeoutRef.current); fastTimeoutRef.current = null }
    if (safetyTimeoutRef.current) { clearTimeout(safetyTimeoutRef.current); safetyTimeoutRef.current = null }

    setProgress(100)
    setTimeout(() => {
      setVisible(false)
      setProgress(0)
    }, 300)
  }, [])

  // Register module-level signals
  useEffect(() => {
    _complete = complete
    _cancelFastTimeout = () => {
      if (fastTimeoutRef.current) { clearTimeout(fastTimeoutRef.current); fastTimeoutRef.current = null }
    }
    return () => { _complete = null; _cancelFastTimeout = null }
  }, [complete])

  useEffect(() => {
    // Skip initial mount
    if (prevPath.current === location.pathname) return
    prevPath.current = location.pathname

    // Start the bar
    setVisible(true)
    setProgress(30)

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev
        return prev + (90 - prev) * 0.15
      })
    }, 100)

    // Fast-complete fallback: if Suspense doesn't trigger within 500ms,
    // the chunk was cached and the page rendered instantly
    fastTimeoutRef.current = setTimeout(complete, 500)

    // Safety: auto-complete after 10s no matter what
    safetyTimeoutRef.current = setTimeout(complete, 10000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (fastTimeoutRef.current) clearTimeout(fastTimeoutRef.current)
      if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current)
    }
  }, [location.pathname, complete])

  if (!visible) return null

  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-[3px]">
      <div
        className="h-full bg-tutara-primary shadow-[0_0_8px_rgba(75,161,157,0.5)] transition-all duration-200 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  )
}
